import { IncentiveOfferSellerParticipationLimits } from 'modules/incentivesMarket/types';
import { IncentiveOfferSellerOfferCap } from '../types/IncentivesMarketBeneficiaryContext';

const normalizeLimits = (
  limits?: IncentiveOfferSellerParticipationLimits
): Required<IncentiveOfferSellerParticipationLimits> => ({
  allowSellAll: limits?.allowSellAll !== false,
  maxUnits: limits?.maxUnits || 0,
  maxPercentUnits: limits?.maxPercentUnits || 0,
  maxPhantoms: limits?.maxPhantoms || 0,
  maxPercentPhantoms: limits?.maxPercentPhantoms || 0,
  maxStocks: limits?.maxStocks || 0,
  maxPercentStocks: limits?.maxPercentStocks || 0,
  maxAmount: limits?.maxAmount || 0,
  maxPercentUnvested:
    limits?.maxPercentUnvested === undefined ||
    limits?.maxPercentUnvested === null
      ? 100
      : limits.maxPercentUnvested,
});

const applyInstrumentCap = (
  baseUnits: number,
  maxNumber: number,
  maxPercent: number
): number => {
  const maxAllowed = maxNumber > 0 ? Math.min(baseUnits, maxNumber) : baseUnits;
  const percentAllowed =
    maxPercent > 0 ? Math.floor((baseUnits * maxPercent) / 100) : baseUnits;

  if (maxNumber > 0 && maxPercent > 0) {
    return Math.max(maxAllowed, percentAllowed);
  }
  if (maxNumber > 0) return maxAllowed;
  if (maxPercent > 0) return percentAllowed;
  return baseUnits;
};

export const calculatePreviewMaxUnitsSellerCanOffer = ({
  sellerParticipationLimits,
  totalAvailableUnits,
  unitPrice = 0,
  planType = 'STOCK_OPTION',
  offerRemainingUnits,
}: {
  sellerParticipationLimits?: IncentiveOfferSellerParticipationLimits;
  totalAvailableUnits: number;
  unitPrice?: number;
  planType?: string;
  offerRemainingUnits?: number;
}): number => {
  const limits = normalizeLimits(sellerParticipationLimits);
  const available = Math.max(0, totalAvailableUnits || 0);
  const remaining =
    offerRemainingUnits === undefined
      ? Number.POSITIVE_INFINITY
      : Math.max(0, offerRemainingUnits || 0);

  if (limits.allowSellAll) {
    return Math.min(available, remaining);
  }

  let currentUnits = Math.floor((available * limits.maxPercentUnvested) / 100);

  if (planType === 'STOCK_OPTION') {
    currentUnits = applyInstrumentCap(
      currentUnits,
      limits.maxStocks,
      limits.maxPercentStocks
    );
  } else if (planType === 'PHANTOM_SHARE') {
    currentUnits = applyInstrumentCap(
      currentUnits,
      limits.maxPhantoms,
      limits.maxPercentPhantoms
    );
  }

  if (limits.maxUnits > 0) {
    currentUnits = Math.min(currentUnits, limits.maxUnits);
  }

  if (limits.maxPercentUnits > 0) {
    currentUnits = Math.min(
      currentUnits,
      Math.floor((available * limits.maxPercentUnits) / 100)
    );
  }

  if (limits.maxAmount > 0 && unitPrice > 0) {
    currentUnits = Math.min(
      currentUnits,
      Math.floor(limits.maxAmount / unitPrice)
    );
  }

  return Math.min(Math.max(0, currentUnits), remaining);
};

export const getSellerLimitsModeLabelKey = (
  limits?: IncentiveOfferSellerParticipationLimits
): string =>
  limits?.allowSellAll === false
    ? 'IncentivesMarketSellerLimitsCustom'
    : 'IncentivesMarketSellerLimitsNoCap';

export const sanitizeSellerParticipationLimitsForPayload = (
  limits?: IncentiveOfferSellerParticipationLimits
): IncentiveOfferSellerParticipationLimits => {
  const normalized = normalizeLimits(limits);

  if (normalized.allowSellAll) {
    return { allowSellAll: true, maxPercentUnvested: 100 };
  }

  const result: IncentiveOfferSellerParticipationLimits = {
    allowSellAll: false,
    maxPercentUnvested: normalized.maxPercentUnvested,
  };

  if (normalized.maxUnits > 0) result.maxUnits = normalized.maxUnits;
  if (normalized.maxPercentUnits > 0) {
    result.maxPercentUnits = normalized.maxPercentUnits;
  }
  if (normalized.maxPhantoms > 0) result.maxPhantoms = normalized.maxPhantoms;
  if (normalized.maxPercentPhantoms > 0) {
    result.maxPercentPhantoms = normalized.maxPercentPhantoms;
  }
  if (normalized.maxStocks > 0) result.maxStocks = normalized.maxStocks;
  if (normalized.maxPercentStocks > 0) {
    result.maxPercentStocks = normalized.maxPercentStocks;
  }
  if (normalized.maxAmount > 0) result.maxAmount = normalized.maxAmount;

  return result;
};

export const buildSellerOfferCapFromContext = (
  sellerOfferCap?: IncentiveOfferSellerOfferCap,
  offerRemainingUnits = 0,
  totalAvailableUnits = 0
): IncentiveOfferSellerOfferCap => {
  if (sellerOfferCap) {
    return sellerOfferCap;
  }

  const remaining = Math.max(0, offerRemainingUnits);
  const effectiveMax = Math.min(Math.max(0, totalAvailableUnits), remaining);

  return {
    maxUnitsSellerCanOffer: effectiveMax,
    offerRemainingUnits: remaining,
    effectiveMax,
  };
};
